import React, { useEffect, useState } from "react";
import Header from "@commons/header";
import Table from "@components/table";
import Admin from "@apis/admin";
import { useSelector } from "react-redux";
import { RootState } from "@store/index";
import Column from "@components/column";
import { useNavigate } from "react-router-dom";
import { Edit2, Trash2, MoreHorizontal } from "lucide-react";
import { toast } from "react-toastify";

import usePermission from "@hooks/usePermission";

const SubAdmins = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const adminsState = useSelector((state: RootState) => state.admins);

  // Robust selector to handle different store states
  const admins = Array.isArray(adminsState?.data)
    ? adminsState.data
    : Array.isArray(adminsState?.items)
      ? adminsState.items
      : [];

  const totalItems = adminsState?.totalItems || admins.length;
  const totalPages = adminsState?.totalPages || 1;
  const [page, setPage] = useState(1);

  const fetchAdmins = async () => {
    try {
      await Admin.subadmin({ state: "admins", params: { page } });
    } catch (error) {
      console.error("Failed to fetch sub admins:", error);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [page]);

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this sub-admin?")) {
      try {
        // We delete first without state update to avoid store corruption
        await Admin.remove({ id });
        toast.success("Sub-admin deleted successfully");
        // Fresh fetch to sync UI with backend
        await fetchAdmins();
      } catch (error) {
        console.error("Failed to delete sub-admin:", error);
        toast.error("Failed to delete sub-admin");
      }
    }
  };

  const columns = [
    {
      title: "ID",
      key: "id",
      render: (item: any) => (
        <span style={{ fontWeight: 600, color: "#4a5568" }}>#{item?.id}</span>
      ),
    },
    {
      title: "Name",
      key: "name",
      render: (item: any) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {item?.avatar ? (
            <img
              src={item.avatar}
              alt={item.name}
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "1px solid #f1f5f9",
              }}
            />
          ) : (
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                background: "#ffeAeb",
                color: "#f23236",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: "14px",
              }}
            >
              {item?.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span
              style={{ fontWeight: 600, color: "#1e293b", fontSize: "14px" }}
            >
              {item?.name}
            </span>
            <span
              style={{
                fontSize: "12px",
                color: "#f23236",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.02em",
              }}
            >
              {item?.roleName || item?.role || "Admin"}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Email",
      key: "email",
      render: (item: any) => (
        <span style={{ color: "#718096" }}>{item?.email}</span>
      ),
    },
    {
      title: "Permissions",
      key: "permissions",
      render: (item: any) => {
        const perms = item?.permissions || [];
        if (perms.length === 0) {
          return (
            <span
              style={{
                color: "#94a3b8",
                fontStyle: "italic",
                fontSize: "13px",
              }}
            >
              No custom permissions
            </span>
          );
        }

        // Group by module for cleaner display if there are many
        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              maxWidth: "450px",
            }}
          >
            {perms.map((p: string, i: number) => (
              <span
                key={i}
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  color: "#475569",
                  padding: "4px 10px",
                  borderRadius: "8px",
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "capitalize",
                }}
              >
                {p.split(".").pop()?.replace(/_/g, " ")}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (item: any) => (
        <div style={{ display: "flex", gap: "8px" }}>
          {hasPermission("sub_admins", "Edit") && (
            <button
              onClick={() =>
                navigate(`/admin/users/sub-admins/edit/${item.id}`)
              }
              style={{
                padding: "6px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                color: "#64748b",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseOver={(e: any) => {
                e.currentTarget.style.color = "#f23236";
                e.currentTarget.style.borderColor = "#f23236";
              }}
              onMouseOut={(e: any) => {
                e.currentTarget.style.color = "#64748b";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <Edit2 size={16} />
            </button>
          )}
          {hasPermission("sub_admins", "Delete") && (
            <button
              onClick={() => handleDelete(item.id)}
              style={{
                padding: "6px",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                color: "#64748b",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseOver={(e: any) => {
                e.currentTarget.style.background = "#fff1f2";
                e.currentTarget.style.color = "#e11d48";
                e.currentTarget.style.borderColor = "#e11d48";
              }}
              onMouseOut={(e: any) => {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.color = "#64748b";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Column>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h1
          style={{
            fontSize: "24px",
            fontWeight: 700,
            color: "#1a202c",
            margin: 0,
          }}
        >
          Sub Admins
        </h1>

        {hasPermission("sub_admins", "Add New") && (
          <button
            onClick={() => navigate("/admin/users/sub-admins/create")}
            style={{
              padding: "10px 24px",
              borderRadius: "8px",
              background: "#f23236",
              color: "#ffffff",
              border: "none",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(242, 50, 54, 0.39)",
              transition: "all 0.2s",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onMouseOver={(e: any) =>
              (e.currentTarget.style.transform = "translateY(-1px)")
            }
            onMouseOut={(e: any) =>
              (e.currentTarget.style.transform = "translateY(0)")
            }
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Team
          </button>
        )}
      </div>

      <div
        style={{
          background: "#ffffff",
          borderRadius: "16px",
          padding: "24px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
          border: "1px solid #f0f4f8",
        }}
      >
        <Table
          data={admins}
          columns={columns}
          totalItems={totalItems}
          totalPages={totalPages}
          pageControlled={page}
          onPageChange={setPage}
          itemsPerPage={30}
          actions={[]}
        />
      </div>
    </Column>
  );
};

export default SubAdmins;
